import { Building, MapPinned, Star, Check } from "lucide-react";

/** Anchor ids for home-page sections (scroll-to / deep links). */
export const HOME_PAGE_SECTION_IDS = {
  hero: "hero",
  workspaceFinder: "workspace-finder",
  topLocations: "top-locations",
  officeOptions: "office-options",
  whyOfficeHub: "why-office-hub",
  popularLocations: "popular-locations",
  partners: "partners",
  listOnOfficeHub: "list-on-office-hub",
  about: "about",
  googleReviews: "google-reviews",
  guidesAndTips: "guides-and-tips",
  officeSearchBanner: "office-search-banner",
} as const;

export type HomePageSectionId =
  (typeof HOME_PAGE_SECTION_IDS)[keyof typeof HOME_PAGE_SECTION_IDS];

export const offices = [
  { id: "1", label: "Private Office", isActive: true },
  { id: "2", label: "Shared Office", isActive: false },
  { id: "3", label: "Coworking Desk", isActive: false },
  { id: "4", label: "Enterprise Office", isActive: false },
  { id: "5", label: "Services Office", isActive: false },
  { id: "6", label: "Managed Office", isActive: false },
  { id: "7", label: "Sublet Office", isActive: false },
  { id: "8", label: "Virtual Office", isActive: false },
];
export const brands = [
  "accenture",
  "sephora",
  "microsoft",
  "paypal",
  "samsung",
  "slack",
  "yahoo"
];
export const officeTypes = [
  {
    id: "coworking",
    title: "Coworking Office",
    description:
      "Collaborate with top industry professionals in our coworking spaces. Professional environment with high-speed internet, meeting rooms, and networking events to boost your visibility.",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/office-types/cowork.avif`,
  },
  {
    id: "private",
    title: "Private Office",
    description:
      "Work in a secluded, distraction-free space to boost your performance! Access basic amenities while maintaining privacy and concentration.",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/office-types/private.avif`,
  },
  {
    id: "enterprise",
    title: "Enterprise Office",
    description:
      "Best for businesses requiring exclusive spaces! Get high-end amenities and high-end amenities for larger teams!",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/office-types/enterprise.avif`,
  },
  {
    id: "managed",
    title: "Managed Office",
    description:
      "Elevate your workspace experience with fully managed workspaces! Get premium professional services and premium facilities tailored to your needs.",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/office-types/managed.avif`,
  },
  {
    id: "shared",
    title: "Shared Office",
    description:
      "Enjoy an exciting work environment that promotes shared learning experiences while maintaining your private space.",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/office-types/shared.avif`,
  },
  {
    id: "virtual",
    title: "Virtual Office",
    description:
      "Go remote with our virtual office! Build your professional presence with business mailing services, and more.",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/office-types/virtual.avif`,
  },

  {
    id: "conventional",
    title: "Conventional Office",
    description:
      "Work in a timeless setup with our conventional office space! Get a personalized workspace in a professional atmosphere for your growing business.",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/office-types/conventional.avif`,
  },
  {
    id: "sublet",
    title: "Sublet Office",
    description:
      "Get a short-term workspace solution with our sublet office! Rent an office space from another business or individual for a specific period.",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/office-types/sublet.avif`,
  },
];

export const locations = [
  {
    id: "south-bank",
    name: "South Bank",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/popular-locations/content1.avif`,
    size: "large",
  },
  {
    id: "paddington-1",
    name: "Paddington",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/popular-locations/content2.avif`,

    size: "small",
  },
  {
    id: "fitzrovia",
    name: "Fitzrovia",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/popular-locations/content3.avif`,
    size: "small",
  },
  {
    id: "kings-cross",
    name: "King's Cross",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/popular-locations/content4.avif`,
    size: "large",
  },
  {
    id: "paddington-2",
    name: "Paddington",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/popular-locations/content5.avif`,
    size: "small",
  },
  {
    id: "canary-wharf",
    name: "Canary Wharf",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/popular-locations/content6.avif`,
    size: "small",
  },
];
export const aboutOfficeHub = [
  {
    icon: Building,
    value: "400%",
    description: "Faster than average office search",
  },
  {
    icon: Star,
    value: "4.9/5",
    description: "Google star rating",
  },
  {
    icon: MapPinned,
    value: "98.8%",
    description: "Market Coverage",
  },
];
export const whyOfficeHub = [
  {
    value: "500+",
    description: "Workspace in London",
  },
  {
    value: "1200+",
    description: "In the United Kingdom",
  },
  {
    value: "4587",
    description: "Worldwide",
  },
];
export const whyOfficeHubTitles = [
  {
    title: "Biggest range of Workspaces:",
    description:
      "We partner with 95% of the world's best coworking, shared and serviced office providers plus we have hundreds of sublet commercial offices on offer.",
  },
  {
    title: "Search with Confidence:",
    description:
      "We only work with the best to ensure the pricing you see is real, the space is as you see it and that all quotes are verified before any tours are booked.",
  },
  {
    title: "Our team are real people:",
    description:
      "Online or over the phone, our team of experts offer a FREE office search brokerage service. We help you shortlist, book tours and negotiate a great deal on your perfect office.",
  },
];
export const reviews = [
  {
    name: "Fadi Tanazi",
    rating: 5,
    score: "10/10",
    content:
      "Daniel was an absolute lifesaver! I needed to find office space quickly and he went above and beyond to make it happen. His efficiency, professionalism, and attention to detail were outstanding. He listened to my needs, found the perfect space, and made the entire process stress-free.he entire process stress-free.Daniel was an absolute lifesaver! I needed to find office space quickly and he went above and beyond to make it happen. His efficiency, professionalism, and attention to detail were outstanding. He listened to my needs, found the perfect space, and made the entire process stress-free.",
  },
  {
    name: "Michael",
    rating: 5,
    score: "10/10",
    content:
      "I had the pleasure of working with Will from Office Hub and I couldn't be more satisfied with the experience I received. From the start, Will was attentive, professional, and truly understood our requirements.",
  },
  {
    name: "Mi Tran",
    rating: 4.5,
    score: "10/10",
    content:
      "We were looking for a new office for our company and the existing process was quite difficult until we came across smooth. Got the office tour scheduled in a couple of minutes and a bunch of options to choose from.",
  },
  {
    name: "Lisa Ronan",
    rating: 4,
    score: "9/10",
    content:
      "Finding the right office space was starting to become a headache until I met James. He was fantastic in providing us with several great options. While the final paperwork took some time, the result was worth it.",
  },
  {
    name: "Amir Suleiman",
    rating: 4.5,
    score: "9.5/10",
    content:
      "Office Hub really made our search for new office space efficient. Susan was our agent, and her insights into the market were incredibly helpful. She found spaces that aligned perfectly with our requirements.",
  },
  {
    name: "Priya Patel",
    rating: 5,
    score: "10/10",
    content:
      "I was blown away by the service provided by Tom. He was incredibly patient with our many requests and changes. The entire process was seamless, and we ended up with an office space that exceeded our expectations.",
  },
  {
    name: "Nina Bauer",
    rating: 4.7,
    score: "9/10",
    content:
      "Excellent experience working with Office Hub. Our agent, Laura, provided comprehensive support throughout the entire process. A great outcome for our company's needs.",
  },
  {
    name: "Carlos Mendez",
    rating: 5,
    score: "10/10",
    content:
      "Maria was outstanding in helping us find new office premises. Her professional demeanor and dedication ensured we found the perfect spot with ease. Highly recommend!",
  },
  {
    name: "Sofia Chen",
    rating: 5,
    score: "10/10",
    content:
      "Greg made finding the right office easy. He was always reachable, understood what we wanted, and had an uncanny ability to anticipate our needs. Fantastic experience!",
  },
  {
    name: "Ali Rahman",
    rating: 4.3,
    score: "8.5/10",
    content:
      "Good service, but encountered some delays during negotiations. Overall, still happy with the final space we secured with Rachel's help. Would consult again.",
  },
  {
    name: "Emily Stone",
    rating: 4.8,
    score: "9.5/10",
    content:
      "Our company had very specific needs and Ben from Office Hub was exceptional in accommodating us. It was a very smooth and rewarding experience.",
  },
  {
    name: "Jackson Lee",
    rating: 4.2,
    score: "8.5/10",
    content:
      "Sara was a tremendous help, though I wish the initial listings matched our brief more closely. Nevertheless, the final choice was ideal.",
  },
  {
    name: "Isabella De Luca",
    rating: 4.9,
    score: "10/10",
    content:
      "Hands down the best property service I have used. Thanks to Tom, the transition to our new office was smooth and timely.",
  },
  {
    name: "Ahmed Al-Farsi",
    rating: 4.6,
    score: "9/10",
    content:
      "Great service provided by Sam. Minimal disruption to our normal activities during the search. The office fits our needs well.",
  },
  {
    name: "Chloe Morgan",
    rating: 5,
    score: "10/10",
    content:
      "James was a gem! His market knowledge and commitment to finding us the perfect space were unparalleled. Highly recommended!",
  },
  {
    name: "Lucas Wong",
    rating: 4.1,
    score: "8/10",
    content:
      "Overall positive experience, but closing the deal took longer than expected. Sarah was very pleasant to work with throughout.",
  },
  {
    name: "Avery Clark",
    rating: 4.4,
    score: "9/10",
    content:
      "Rebecca provided numerous great options, but it was her attention to detail that sealed the deal for us. Thank you!",
  },
  {
    name: "Mia Carver",
    rating: 5,
    score: "10/10",
    content:
      "The entire process of finding a new office was handled impeccably by Dylan. We couldn't have asked for a better service.",
  },
  {
    name: "Oliver Griffin",
    rating: 4.7,
    score: "9.5/10",
    content:
      "Daniel's expertise made this a stress-free experience. His recommendations were spot on for our growing business needs.",
  },
  {
    name: "Victoria Hale",
    rating: 4.5,
    score: "9/10",
    content:
      "Great support by Emma, who made sure we didn't have to compromise on any of our major requirements.Great support by Emma, who made sure we didn't have to compromise on any of our major requirements.",
  },
  {
    name: "Aaron Smith",
    rating: 4.9,
    score: "10/10",
    content:
      "Top-notch service from Liam at Office Hub. Every potential obstacle was resolved promptly and professionally.",
  },
  {
    name: "Olivia Martinez",
    rating: 5,
    score: "10/10",
    content:
      "Couldn't be happier with the office space and the service Jess provided. Her enthusiasm and dedication were impressive.",
  },
  {
    name: "Ethan Wright",
    rating: 4.3,
    score: "8.5/10",
    content:
      "Working with Office Hub was generally positive. We faced some challenges but Kevin tackled them effectively.",
  },
  {
    name: "Charlotte Baker",
    rating: 5,
    score: "10/10",
    content:
      "Perfect result thanks to Henry's efforts. He listened and executed perfectly, delivering exactly what we needed.Perfect result thanks to Henry's efforts. He listened and executed perfectly, delivering exactly what we needed.Perfect result thanks to Henry's efforts. He listened and executed perfectly, delivering exactly what we needed.",
  },
  {
    name: "Sebastian Turner",
    rating: 4.7,
    score: "9.5/10",
    content:
      "Excellent negotiator and communicator. Peter made sure that all our interests were well represented and secured.",
  },
  {
    name: "Amelia Walker",
    rating: 4.6,
    score: "9/10",
    content:
      "Karen at Office Hub was incredibly proactive. We felt confident with her leading our office search.",
  },
];
export const guideAndTips = [
  {
    id: "south-bank",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/popular-locations/content1.avif`,
    size: "large",
    description:
      "How many square meters do you need for your next office space ?",
  },
  {
    id: "paddington-1",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/popular-locations/content2.avif`,
    size: "small",
    description: "What is service charge when renting an office space?",
  },
  {
    id: "fitzrovia",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/popular-locations/content3.avif`,
    size: "small",
    description: "What is the office space cost in Sydney, Australia?",
  },
  {
    id: "kings-cross",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/popular-locations/content4.avif`,
    size: "large",
    description:
      'What does the term "service charge" refer to when renting an office space, and what does it typically cover?',
  },
  {
    id: "paddington-2",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/popular-locations/content5.avif`,
    size: "small",
    description: "Difference b/w Services Office and Managed Offices ?",
  },
  {
    id: "canary-wharf",
    image: `${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/popular-locations/content6.avif`,
    size: "small",
    description: "What is service charge when renting an office space?",
  },
];

export const howToSignIn = [
  {
    step: "1",
    title: "Tell us your dream office space",
    description:
      "Our flexperts will get in touch with you to understand your workspace needs and provide you with a curated shortlist of the best options.",
  },
  {
    step: "2",
    title: "Attend the workspace tour",
    description:
      "Once you've found the right office space, attend the workspace tour that our team will schedule for you! Assess the space and make an informed decision.",
  },
  {
    step: Check,
    title: "Sign in and move right away",
    description:
      "If everything looks good on your end, sign the lease, and the provider will prepare the workspace for you to move in no time!",
  },
];
export const ExploreOfficeTypes = [
  { label: "Office Space", spaceType: 'Office Space' },
  { label: "Private Office", spaceType: 'Private Office' },
  { label: "Coworking Desk", spaceType: 'Coworking Desk' },
  { label: "Shared Office", spaceType: 'Shared Office' },
  { label: "Enterprise Suite", spaceType: 'Enterprise Suite' },
  { label: "Virtual Office", spaceType: 'Virtual Office' },
  { label: "Sublet / Conventional Office", spaceType: 'Sublet Office' },
  { label: "Managed Space", spaceType: 'Managed Space' },
];

export const cityData = [
  {
    city: "London",
    locations: [
      "Canary Wharf",
      "The City of London",
      "South Bank",
      "Shoreditch",
      "King's Cross",
      "Fitzrovia",
      "Paddington",
      "Central Station",
    ],
  },
  {
    city: "Manchester",
    locations: [
      "Spinningfields",
      "MediaCityUK",
      "Northern Quarter",
      "Ancoats",
      "Oxford Road Corridor",
      "Castlefield",
      "Deansgate",
    ],
  },
  {
    city: "Birmingham",
    locations: [
      "Business District",
      "Brindleyplace",
      "Birmingham Business Park",
      "Jewellery Quarter",
      "Digbeth",
      "Edgbaston",
      "Soho",
    ],
  },
  {
    city: "Edinburgh",
    locations: [
      "Exchange District",
      "Quartermile",
      "Edinburgh Park",
      "Leith",
      "Princes Street",
      "Haymarket",
      "West End",
    ],
  },
  {
    city: "Glasgow",
    locations: [
      "City Centre",
      "Merchant City",
      "Pacific Quay",
      "West Regent Street",
      "Charing Cross",
      "Broomielaw",
    ],
  },
  {
    city: "Leeds",
    locations: [
      "Wellington Place",
      "Leeds Dock",
      "Park Square",
      "Holbeck Urban Village",
      "South Bank Leeds",
      "The Headrow",
      "Whitehall Road",
    ],
  },
  {
    city: "Edinburgh",
    locations: [
      "Exchange District",
      "Quartermile",
      "Edinburgh Park",
      "Leith",
      "Princes Street",
      "Haymarket",
      "West End",
    ],
  },
  {
    city: "Glasgow",
    locations: [
      "City Centre",
      "Merchant City",
      "Pacific Quay",
      "West Regent Street",
      "Charing Cross",
      "Broomielaw",
    ],
  },
  {
    city: "London",
    locations: [
      "Canary Wharf",
      "The City of London",
      "South Bank",
      "Shoreditch",
      "King's Cross",
      "Fitzrovia",
      "Paddington",
      "Central Station",
    ],
  },
  {
    city: "Manchester",
    locations: [
      "Spinningfields",
      "MediaCityUK",
      "Northern Quarter",
      "Ancoats",
      "Oxford Road Corridor",
      "Castlefield",
      "Deansgate",
    ],
  },
  {
    city: "Birmingham",
    locations: [
      "Business District",
      "Brindleyplace",
      "Birmingham Business Park",
      "Jewellery Quarter",
      "Digbeth",
      "Edgbaston",
      "Soho",
    ],
  },
  {
    city: "Edinburgh",
    locations: [
      "Exchange District",
      "Quartermile",
      "Edinburgh Park",
      "Leith",
      "Princes Street",
      "Haymarket",
      "West End",
    ],
  },
  {
    city: "Glasgow",
    locations: [
      "City Centre",
      "Merchant City",
      "Pacific Quay",
      "West Regent Street",
      "Charing Cross",
      "Broomielaw",
    ],
  },
  {
    city: "Leeds",
    locations: [
      "Wellington Place",
      "Leeds Dock",
      "Park Square",
      "Holbeck Urban Village",
      "South Bank Leeds",
      "The Headrow",
      "Whitehall Road",
    ],
  },
  {
    city: "Edinburgh",
    locations: [
      "Exchange District",
      "Quartermile",
      "Edinburgh Park",
      "Leith",
      "Princes Street",
      "Haymarket",
      "West End",
    ],
  },
  {
    city: "Glasgow",
    locations: [
      "City Centre",
      "Merchant City",
      "Pacific Quay",
      "West Regent Street",
      "Charing Cross",
      "Broomielaw",
    ],
  },
  {
    city: "London",
    locations: [
      "Canary Wharf",
      "The City of London",
      "South Bank",
      "Shoreditch",
      "King's Cross",
      "Fitzrovia",
      "Paddington",
      "Central Station",
    ],
  },
  {
    city: "Manchester",
    locations: [
      "Spinningfields",
      "MediaCityUK",
      "Northern Quarter",
      "Ancoats",
      "Oxford Road Corridor",
      "Castlefield",
      "Deansgate",
    ],
  },
  {
    city: "Birmingham",
    locations: [
      "Business District",
      "Brindleyplace",
      "Birmingham Business Park",
      "Jewellery Quarter",
      "Digbeth",
      "Edgbaston",
      "Soho",
    ],
  },
  {
    city: "Edinburgh",
    locations: [
      "Exchange District",
      "Quartermile",
      "Edinburgh Park",
      "Leith",
      "Princes Street",
      "Haymarket",
      "West End",
    ],
  },
  {
    city: "Glasgow",
    locations: [
      "City Centre",
      "Merchant City",
      "Pacific Quay",
      "West Regent Street",
      "Charing Cross",
      "Broomielaw",
    ],
  },
  {
    city: "Leeds",
    locations: [
      "Wellington Place",
      "Leeds Dock",
      "Park Square",
      "Holbeck Urban Village",
      "South Bank Leeds",
      "The Headrow",
      "Whitehall Road",
    ],
  },
  {
    city: "Edinburgh",
    locations: [
      "Exchange District",
      "Quartermile",
      "Edinburgh Park",
      "Leith",
      "Princes Street",
      "Haymarket",
      "West End",
    ],
  },
  {
    city: "Glasgow",
    locations: [
      "City Centre",
      "Merchant City",
      "Pacific Quay",
      "West Regent Street",
      "Charing Cross",
      "Broomielaw",
    ],
  },
];

export const getCountrySpecificMeta = (country: string): CountryMeta => {
  interface CountryMeta {
    metaTitle: string;
    metaDescription: string;
    aboutUs?: string;
  }

  const countryMap: Record<string, CountryMeta> = {
    'us': {
      metaTitle: 'Rent Coworking & Serviced Offices in the USA | Office Hub',
      metaDescription: 'Discover coworking and serviced offices across the USA. From New York to Los Angeles, find flexible workspaces with expert help at no cost.',
      aboutUs: `At Office Hub United States, we connect professionals and businesses with flexible office spaces across the country.
      From coworking hubs and serviced offices to private suites and enterprise spaces, we make it simple to find the right workspace for your team.<br><br>
      Our goal is to make office leasing stress-free. All listings are verified, pricing is transparent, and most spaces offer short, flexible terms that grow with your business.<br><br>
      Reach out to our expert team today to explore premium office spaces in New York, Los Angeles, Chicago, and major business cities across the US.`
    },
    'uk': {
      metaTitle: 'Find Flexible Coworking, Private and Shared Office Spaces Across the UK – Office Hub',
      metaDescription: 'Explore coworking, private, serviced and managed offices across the UK. Compare flexible office options nationwide and find your perfect workspace with Office Hub.'
    },
    'au': {
      metaTitle: 'Rent Coworking & Serviced Offices in Australia | Office Hub',
      metaDescription: "Find coworking and serviced offices in Sydney, Melbourne, and beyond. Office Hub helps you compare, tour, and rent the perfect flexible workspace.",
      aboutUs: `At Office Hub Australia, we make it easy to find coworking, serviced, and private offices in Sydney, Melbourne, Brisbane, and across the country.
      Each space offers the flexibility and quality that growing teams need.<br><br> We believe in choice, transparency, and simplicity.
      Our local experts will help you compare options, book tours, and secure the perfect space at no cost.<br><br> Start your search today and find the right workspace for your business in Australia.`
    },
    'sg': {
      metaTitle: 'Rent Coworking & Serviced Offices in Singapore | Office Hub',
      metaDescription: 'Find coworking and serviced offices in Singapore with Office Hub. Compare flexible workspaces, get expert help, and rent your ideal office today.',
      aboutUs: `Office Hub Singapore connects teams to flexible office spaces across the city.<br>
      From coworking hubs to private and serviced offices, our listings are designed to suit every stage of business growth.<br><br>
      We make workspace flexibility simple with adjustable lease terms and move-in-ready options across Singapore's top business districts.<br><br>
      Reach out to our local experts to explore premium office spaces that fit your goals and budget.`
    },
    'in': {
      metaTitle: ' Coworking & Serviced Offices in India | Office Hub',
      metaDescription: 'Discover coworking and serviced offices in India’s top cities like Mumbai, Bangalore, and Delhi. Flexible spaces, expert support, and zero fees.',
      aboutUs: `At Office Hub India, we specialize in helping businesses discover flexible workspaces in top cities such as Mumbai, Bangalore, and Delhi.
      Choose from coworking, serviced, or private offices equipped with modern amenities.<br><br>
      Our goal is to make office hunting effortless with flexible lease terms and verified listings that match your exact needs.<br><br>
      Speak with our local experts today and find your perfect workspace anywhere in India.`
    }
  };

  return countryMap[country] || null;
};